import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type LlamadoDetalle } from "@/lib/api";
import { aPayloadLlamado, formLlamado, validarLlamado, type ErroresLlamado, type FormLlamado } from "@/lib/aportes";
import { useVistaPrevia } from "./useLlamadosTab";

const SIN_ERRORES: ErroresLlamado = {};
type Dialogo = "editar" | "borrar" | null;

/** Lógica de la ficha de un llamado (sin JSX). Admin edita y borra; el contador sólo mira. */
export function useLlamadoTab(id: number) {
  const { fid, membresia } = useContextoApp();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/llamados`;
  const ruta = `${base}/${id}`;
  const esAdmin = membresia.rol === "admin";
  const ficha = useApiResource<LlamadoDetalle>(ruta);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormLlamado>(() => formLlamado());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionEditar = useAsyncAction((datos: ReturnType<typeof aPayloadLlamado>) => api.patch<LlamadoDetalle>(ruta, datos));
  const accionBorrar = useAsyncAction(() => api.delete<void>(ruta).then(() => true));
  const errores = validarLlamado(form);
  // La vista previa sólo tiene sentido si el reparto puede cambiar: si el fiduciante edita algo que no
  // sea monto o fecha (ej. descripción), el reparto del llamado ya cargado sigue vigente y no hace falta
  // pedirlo de nuevo al backend.
  const original = ficha.data ? formLlamado(ficha.data) : null;
  // Con montos a mano (Pedido de capital) el reparto no cambia nunca: el monto no se edita y la fecha no reparte.
  const cambioReparto =
    original !== null &&
    !ficha.data?.montos_a_mano && (form.monto_usd !== original.monto_usd || form.fecha !== original.fecha || form.cubierto !== original.cubierto);
  const { vistaPrevia, errorVistaPrevia } = useVistaPrevia(base, form, dialogo === "editar" && cambioReparto, id);

  const abrirEditar = () => {
    if (!ficha.data) return;
    setForm(formLlamado(ficha.data));
    setMostrarErrores(false);
    accionEditar.limpiarError();
    setDialogo("editar");
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    if (await accionEditar.run(aPayloadLlamado(form))) {
      setDialogo(null);
      ficha.recargar();
    }
  };

  const confirmarBorrar = async () => {
    if (await accionBorrar.run()) void navegar("/llamados");
  };

  return {
    esAdmin,
    llamado: ficha.data ?? null,
    cargando: ficha.loading && !ficha.data,
    errorCarga: ficha.data ? null : (ficha.error?.message ?? null),
    dialogo,
    cerrar: () => setDialogo(null),
    abrirEditar,
    abrirBorrar: () => {
      accionBorrar.limpiarError();
      setDialogo("borrar");
    },
    form,
    cambiar: <K extends keyof FormLlamado>(campo: K, valor: FormLlamado[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    vistaPrevia,
    errorVistaPrevia,
    guardar,
    guardando: accionEditar.loading,
    errorGuardar: accionEditar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
